import { useState, useEffect, startTransition } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Loading from "../../components/Loading";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  AppBar,
  Toolbar,
  Grid,
  Avatar,
  Stack,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ThemeContextProvider from "../../providers/ThemeProvider";
import { useChangeOrganizationId } from "../../providers/OrganizationProvider";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import Setting from "../../components/Setting";
import { useMenu } from "../../hooks/useMenu";
import { useTheme } from "../../hooks/useTheme";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import useMedia from "../../hooks/useMedia";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import axiosInstance from "../../utilities/axios";
import CardMembershipIcon from "@mui/icons-material/CardMembership";

const OrganizationListPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOrg, setMenuOrg] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const user = useAuthUser() || {};
  const smLayout = useMedia("(min-width: 576px)");
  const changeOrganizationId = useChangeOrganizationId();
  const signOut = useSignOut();
  const setSearchParams = useSearchParams()[1];

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const {
    anchorEl: anchorElProfile,
    openMenu: openProfileMenu,
    closeMenu: closeProfileMenu,
  } = useMenu();

  const {
    anchorEl: anchorElSettings,
    openMenu: openSettingsMenu,
    closeMenu: closeSettingsMenu,
  } = useMenu();

  const { toggleTheme, mode } = useTheme();

  const getId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value._id) return String(value._id);
    if (value.$oid) return String(value.$oid);
    return String(value);
  };

  const getUserIds = () => {
    return [
      getId(user._id),
      getId(user.id),
      getId(user.userId),
      getId(user.accountUserId),
      getId(user.worksuiteUserId),
    ].filter(Boolean);
  };

  const isOrgAdmin = (org) => {
    const userIds = getUserIds();

    const ownerIds = [
      getId(org.ownerId),
      getId(org.owner),
      getId(org.userId),
      getId(org.worksuiteUserId),
      getId(org.worksuiteOwnerId),
    ].filter(Boolean);

    const typeIsOwner = String(org.type || "").toLowerCase() === "owner";
    const roleIsAdmin = String(org.role || "").toLowerCase() === "admin";

    const userMatchesOwner = userIds.some((id) => ownerIds.includes(id));

    return typeIsOwner || roleIsAdmin || userMatchesOwner;
  };

  const getMemberCount = (org) => {
    if (Array.isArray(org.members)) return org.members.length;
    if (Array.isArray(org.membersData)) return org.membersData.length;
    if (org.totalEmployees) return org.totalEmployees;
    return 1;
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleDelete = (organizationName) => {
    setSelectedOrg(organizationName);
    setConfirmName("");
    setOpen(true);
  };

  const signout = () => {
    signOut();
    localStorage.removeItem("selectedProject");
    setSearchParams({}, { replace: true });
    window.location.reload();
  };

  const confirmDelete = async () => {
    if (confirmName === selectedOrg) {
      setIsDeleting(true);

      try {
        await axiosInstance.delete(`/api/v2/organizations`, {
          headers: {
            "Content-Type": "application/json",
          },
          data: { name: selectedOrg },
        });

        setOrganizations((prevOrgs) =>
          prevOrgs.filter((org) => org.name !== selectedOrg)
        );

        setSnackbarSeverity("success");
        setSnackbarMessage("Organization deleted successfully!");
        setSnackbarOpen(true);

        setOpen(false);

        if (organizations.length <= 1) {
          navigate("/walk-through");
        }
      } catch (error) {
        setSnackbarSeverity("error");
        setSnackbarMessage(
          "Error deleting organization. Please try again later."
        );
        setSnackbarOpen(true);
      } finally {
        setIsDeleting(false);
      }
    } else {
      setSnackbarSeverity("error");
      setSnackbarMessage("Organization name does not match.");
      setSnackbarOpen(true);
    }
  };

  const handleOrganizationClick = (organization) => {
    const orgForStorage = { ...organization };
    delete orgForStorage.imageUrl;

    localStorage.setItem("org", JSON.stringify(orgForStorage));
    changeOrganizationId(organization._id);

    startTransition(() =>
      organizations.length > 0
        ? navigate("/list-project")
        : navigate("/project-selection", { state: { organization } })
    );
  };

  const handleMenuOpen = (event, organization) => {
    setMenuAnchor(event.currentTarget);
    setMenuOrg(organization);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuOrg(null);
  };

  const handleDeleteFromMenu = () => {
    if (menuOrg) {
      handleDelete(menuOrg.name);
      handleMenuClose();
    }
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user?._id) return;

      try {
        const response = await axiosInstance.get(
          `/api/v2/organizations/${user._id}`
        );

        const orgs = response.data.organizations || [];

        if (orgs.length > 0) {
          setOrganizations(orgs);

          const storageOrgs = orgs.map(({ imageUrl, ...noImage }) => noImage);
          localStorage.setItem("allOrg", JSON.stringify(storageOrgs));
        } else {
          navigate("/createOrganization");
        }
      } catch (error) {
        navigate("/createOrganization");
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchOrganizations();
  }, [navigate, user?._id]);

  if (isLoading || isDeleting) {
    return <Loading message="Loading..." />;
  }

  return (
    <ThemeContextProvider>
      <AppBar
        elevation={0}
        component={Box}
        position="sticky"
        sx={{
          padding: "0 15px",
          backgroundColor: mode === "dark" ? "#000" : "#fefefe",
        }}
      >
        <Toolbar
          sx={{
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            "&": {
              minHeight: "64px",
              px: 1,
            },
          }}
        >
          <Grid container alignItems="center" columnSpacing={2}>
            <Grid item xs display={{ xs: "none", sm: "block" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={0}
                sx={{ gap: "10px" }}
              >
                <IconButton>
                  <AddIcon
                    fontSize="medium"
                    onClick={() => navigate("/createOrganization")}
                  />
                </IconButton>

                <IconButton onClick={openSettingsMenu}>
                  <SettingsIcon fontSize="medium" />
                </IconButton>

                <Menu
                  anchorEl={anchorElSettings}
                  open={Boolean(anchorElSettings)}
                  onClose={closeSettingsMenu}
                  transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                  anchorOrigin={{
                    horizontal: "right",
                    vertical: "bottom",
                  }}
                >
                  <Setting
                    mode={mode}
                    toggleTheme={toggleTheme}
                    closeSettingsMenu={closeSettingsMenu}
                  />
                </Menu>
              </Stack>
            </Grid>

            {false && (
              <Grid item>
                {smLayout ? (
                  <IconButton
                    onClick={openProfileMenu}
                    sx={{
                      borderWidth: "2px",
                      borderStyle: "solid",
                      borderColor: "primary.main",
                      p: "3px",
                    }}
                  >
                    <Avatar
                      alt="User"
                      src="https://media.istockphoto.com/id/1300845620/vector/user-icon-flat-isolated-on-white-background-user-symbol-vector-illustration.jpg?s=612x612&w=0&k=20&c=yBeyba0hUkh14_jgv1OKqIH0CCSWU_4ckRkAoy2p73o="
                      sx={{
                        cursor: "pointer",
                        width: 35,
                        height: 35,
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    sx={{
                      borderWidth: "2px",
                      borderStyle: "solid",
                      borderColor: "primary.main",
                      p: "3px",
                    }}
                  >
                    <Avatar
                      alt="User"
                      src="https://media.istockphoto.com/id/1300845620/vector/user-icon-flat-isolated-on-white-background-user-symbol-vector-illustration.jpg?s=612x612&w=0&k=20&c=yBeyba0hUkh14_jgv1OKqIH0CCSWU_4ckRkAoy2p73o="
                      sx={{
                        cursor: "pointer",
                        width: 30,
                        height: 30,
                      }}
                    />
                  </IconButton>
                )}

                <Menu
                  anchorEl={anchorElProfile}
                  open={Boolean(anchorElProfile)}
                  onClose={closeProfileMenu}
                  sx={{
                    ".MuiPaper-root.MuiMenu-paper.MuiPopover-paper": {
                      width: "min(100%, 320px)",
                      boxShadow:
                        "rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px",
                      border: "1px solid #00000017",
                      bgcolor: "custom.menu",
                      px: 0.5,
                      pt: 1.5,
                    },
                  }}
                >
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    flexWrap="nowrap"
                  >
                    <Grid item>
                      <>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 70,
                            top: 75,
                            zIndex: "1200",
                          }}
                        >
                          <Link to="https://myaccount.clikkle.com/">
                            <IconButton
                              disableFocusRipple
                              disableRipple
                              disablePadding
                              sx={{
                                p: 0.4,
                                background: "#EFF0F0",
                              }}
                            >
                              <EditIcon
                                fontSize="small"
                                sx={{
                                  color: "black",
                                  "&:hover": {
                                    color: "black",
                                  },
                                }}
                              />
                            </IconButton>
                          </Link>
                        </Box>
                        <Avatar
                          alt="User"
                          src="https://media.istockphoto.com/id/1300845620/vector/user-icon-flat-isolated-on-white-background-user-symbol-vector-illustration.jpg?s=612x612&w=0&k=20&c=yBeyba0hUkh14_jgv1OKqIH0CCSWU_4ckRkAoy2p73o="
                          sx={{
                            cursor: "pointer",
                            position: "relative",
                            width: 100,
                            height: 100,
                          }}
                        />
                      </>
                    </Grid>

                    <Grid item xs={8}>
                      <Typography
                        variant="subtitle1"
                        component="div"
                        fontWeight={600}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {user?.firstName + " " + user?.lastName}
                      </Typography>

                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user?.email}
                      </Typography>

                      <Typography
                        variant="caption"
                        component="a"
                        href="#"
                        color="primary.main"
                        display="block"
                      >
                        My Clikkle account
                      </Typography>

                      <Typography
                        variant="caption"
                        component="a"
                        href="/profile"
                        color="primary.main"
                        display="block"
                      >
                        My Profile
                      </Typography>
                    </Grid>
                  </Grid>

                  <Stack direction="row" mt={2}>
                    <Button variant="text" fullWidth>
                      Add account
                    </Button>
                    <Button variant="text" onClick={signout} fullWidth>
                      Sign out
                    </Button>
                  </Stack>
                </Menu>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          textAlign: "left",
          padding: "75px 40px",
          height: "100vh",
          overflowX: "hidden",
          backgroundColor: mode === "dark" ? "#000" : "#fefefe",
        }}
      >
        <h1
          style={{
            fontSize: "2.15rem",
            fontWeight: "400",
            marginBottom: "5px",
            marginTop: "15px",
            lineHeight: "1.235",
            letterSpacing: "0.00735em",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          Organization List
        </h1>

        <Typography
          sx={{
            fontSize: "14px",
            marginBottom: "30px",
            color: "rgb(168, 162, 158)",
            lineHeight: "1.5",
            "@media (max-width: 600px)": { width: "100%" },
          }}
        >
          Project organization refers to the style of coordination,
          communication, and management a team uses throughout a project’s
          lifecycle.
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: "rgb(168, 162, 158)",
            marginBottom: "20px",
            fontSize: "1rem",
          }}
        >
          Total Organization
          <p style={{ fontSize: "2.125rem", margin: 0 }}>
            {organizations.length}
          </p>
        </Typography>

        <Box
          sx={{
            width: "100%",
            overflowX: { xs: "auto", md: "visible" },
          }}
        >
          <Box sx={{ minWidth: 600 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1.5fr 1fr 0.5fr",
                padding: "10px",
                fontWeight: "bold",
                marginTop: "36px",
              }}
            >
              <Typography sx={{ fontWeight: "bold" }}>Organization</Typography>
              <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
                Status
              </Typography>
              <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
                Subscription
              </Typography>
              <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
                Users
              </Typography>
              <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
                Action
              </Typography>
            </Box>

            <Divider sx={{ minWidth: 525, marginBottom: "30px" }} />

            {organizations.map((org, index) => {
              const isAdmin = isOrgAdmin(org);
              const memberCount = getMemberCount(org);

              return (
                <Box
                  key={org._id || index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1.5fr 1fr 0.5fr",
                    padding: "10px 0",
                    alignItems: "center",
                  }}
                >
                  <Box
                    onClick={() => handleOrganizationClick(org)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={org.imageUrl}
                      alt={org.name}
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        marginRight: "15px",
                      }}
                    />
                    <Typography
                      sx={{ "&:hover": { color: "rgb(2, 120, 184)" } }}
                    >
                      {org.name}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      className={`p-1 rounded-[20px] flex items-center gap-2 ${
                        isAdmin
                          ? mode === "dark"
                            ? "bg-[#102a43] px-3"
                            : "bg-[#cce6ff] px-3"
                          : mode === "dark"
                          ? "bg-[#1d3517] px-4"
                          : "bg-[#d9f1d3] px-4"
                      }`}
                      sx={{
                        color: mode === "dark" ? "white" : "black",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "120px",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: isAdmin ? "#0080FF" : "#39ff00",
                        }}
                      />
                      {isAdmin ? "Admin" : "Member"}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    {isAdmin && (
                      <CardMembershipIcon style={{ color: "#0080FF" }} />
                    )}
                    {isAdmin ? org.plan || "No Plan" : ""}
                  </Typography>

                  <Typography sx={{ textAlign: "center" }}>
                    {isAdmin ? memberCount : ""}
                  </Typography>

                  <Box sx={{ textAlign: "center" }}>
                    {isAdmin && (
                      <IconButton onClick={(e) => handleMenuOpen(e, org)}>
                        <MoreVertIcon sx={{ transform: "rotate(90deg)" }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleDeleteFromMenu}>
            <DeleteIcon sx={{ marginRight: "8px" }} />
            Delete
          </MenuItem>
        </Menu>

        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogContent>
            <Typography
              sx={{
                color: "white",
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "16px",
              }}
            >
              Delete <span>{selectedOrg}</span>
            </Typography>

            <Typography
              sx={{
                fontSize: "17px",
                marginBottom: "8px",
              }}
            >
              Are you sure you want to delete the organization{" "}
              <span style={{ color: "#42a5f5" }}>{selectedOrg}</span>?
            </Typography>

            <Typography
              sx={{
                color: "red",
                fontSize: "13px",
                marginBottom: "16px",
                fontWeight: 500,
              }}
            >
              This action cannot be undone.
            </Typography>

            <Typography sx={{ fontSize: "14px", marginBottom: "8px" }}>
              Please type the organization name to confirm:
            </Typography>

            <TextField
              sx={{
                marginBottom: "24px",
                borderRadius: "4px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "gray",
                  },
                  "&:hover fieldset": {
                    borderColor: "white",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#42a5f5",
                  },
                },
                input: { color: "white" },
              }}
              fullWidth
              variant="outlined"
              placeholder="Type organization name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
            />
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: "flex-end",
              padding: "0px 8px",
              marginBottom: "8px",
            }}
          >
            <Button
              onClick={() => setOpen(false)}
              sx={{
                color: "#42a5f5",
                borderStyle: "solid",
                borderWidth: "1px",
                borderColor: "#42a5f5",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Close
            </Button>

            <Button
              onClick={confirmDelete}
              sx={{
                color: "#fff",
                backgroundColor: "#42a5f5",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  color: "#42a5f5",
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeContextProvider>
  );
};

export default OrganizationListPage;